'use client';

import { useCallback, useMemo } from 'react';
import {
  Menu,
  Avatar,
  Button,
  MetaDefault,
  toasterApi,
} from '@terra/ui-design-system';
import { UserIcon } from '@terra/ui-icons';
import { HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useUsers } from '../../hooks/useUsers';
import { useUpdateFinding } from '../../hooks/api/useFindings';
import { terraProxyApiClient } from '../../clients/testerClient';

type AssigneeSelectorBaseProps = {
  selectedId?: string;
  disabled?: boolean;
  showName?: boolean;
  onAssigneeChange?: (assigneeId: string | undefined) => void;
};

type SignalAssigneeSelectorProps = AssigneeSelectorBaseProps & {
  kind: 'signal';
  signalId: string;
};

type FindingAssigneeSelectorProps = AssigneeSelectorBaseProps & {
  kind: 'finding';
  findingId: string;
};

export type AssigneeSelectorProps =
  | SignalAssigneeSelectorProps
  | FindingAssigneeSelectorProps;

const resolveSelectedUser = ({
  users,
  selectedId,
}: {
  users: { id: string; name: string; email: string; avatar?: string }[];
  selectedId?: string;
}) => {
  if (!selectedId?.trim()) {
    return undefined;
  }

  return users.find(
    (user) =>
      user.id === selectedId ||
      user.name === selectedId ||
      user.email === selectedId,
  );
};

export function AssigneeSelector(props: AssigneeSelectorProps) {
  const { selectedId, disabled, showName, onAssigneeChange, kind } = props;
  const tFinding = useTranslations('designSystem.findings.editDrawer.assignee');
  const tSignal = useTranslations('designSystem.signals.assignee');
  const t = kind === 'finding' ? tFinding : tSignal;
  const { data: users } = useUsers();
  const { updateFinding } = useUpdateFinding();

  const selectedAssignee = useMemo(
    () =>
      resolveSelectedUser({
        users: users ?? [],
        selectedId,
      }),
    [users, selectedId],
  );

  const handleAssigneeChange = useCallback(
    async (assigneeId: string | undefined) => {
      const previousId = selectedId;

      onAssigneeChange?.(assigneeId);

      try {
        if (props.kind === 'finding') {
          const response = await updateFinding({
            findingId: props.findingId,
            patch: { assignedTo: assigneeId ?? '' },
          });

          if (response.status !== 200) {
            throw new Error(`Unexpected status ${response.status}`);
          }
        } else {
          await terraProxyApiClient.updateSignal(props.signalId, {
            assigneeId: assigneeId || null,
          });
        }

        toasterApi.success({
          title: assigneeId ? t('updatedTitle') : t('removedTitle'),
          description: assigneeId
            ? t('updatedDescription')
            : t('removedDescription'),
        });
      } catch (error) {
        onAssigneeChange?.(previousId);
        console.error('Failed to update assignee:', error);
        toasterApi.error({
          title: t('failedTitle'),
          description: t('failedDescription'),
        });
      }
    },
    [selectedId, onAssigneeChange, props, updateFinding, t],
  );

  return (
    <Menu.Root
      positioning={{
        placement: 'bottom-start',
        strategy: 'fixed',
      }}
      lazyMount
      unmountOnExit
    >
      <Menu.Trigger asChild disabled={disabled}>
        <Button size="xs" variant="ghost" aria-label={t('selectAriaLabel')}>
          {selectedAssignee ? (
            <HStack gap="2" alignItems="center">
              <Avatar
                size="xs"
                name={selectedAssignee.name}
                src={
                  selectedAssignee.avatar &&
                  selectedAssignee.avatar.trim() !== ''
                    ? selectedAssignee.avatar
                    : undefined
                }
              />
              {showName && <MetaDefault>{selectedAssignee.name}</MetaDefault>}
            </HStack>
          ) : (
            <HStack gap="2" alignItems="center">
              <UserIcon size={14} />
              {showName && <MetaDefault>{t('assign')}</MetaDefault>}
            </HStack>
          )}
        </Button>
      </Menu.Trigger>
      <Menu.Positioner>
        <Menu.Content maxHeight="300px" overflowY="auto">
          <Menu.ItemGroup>
            <Menu.ItemGroupLabel fontSize="xs">
              {t('menuLabel')}
            </Menu.ItemGroupLabel>
            <Menu.Separator />
            {selectedAssignee && (
              <>
                <Menu.Item
                  value="unassign"
                  onClick={() => handleAssigneeChange(undefined)}
                >
                  <HStack gap="2">
                    <UserIcon size={14} />
                    <MetaDefault>{t('unassign')}</MetaDefault>
                  </HStack>
                </Menu.Item>
                <Menu.Separator />
              </>
            )}
            {users?.map((user) => (
              <Menu.Item
                key={user.id}
                value={user.id}
                onClick={() => handleAssigneeChange(user.id)}
              >
                <HStack gap="2">
                  <Avatar
                    name={user.name}
                    size="xs"
                    src={
                      user.avatar && user.avatar.trim() !== ''
                        ? user.avatar
                        : undefined
                    }
                  />
                  <MetaDefault>{user.name}</MetaDefault>
                </HStack>
              </Menu.Item>
            ))}
          </Menu.ItemGroup>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
}
